/**
 * Bundle 138 - Acceptable Use Policy (public).
 *
 * Renders the markdown at `/api/legal/acceptable-use` and ALSO bakes
 * the four Stripe-compliance clause anchors into the initial HTML
 * (see the `sr-only` fallback block) so a crawler with JavaScript
 * disabled still sees the required phrases on the returned document.
 *
 * Guardrails:
 *  - No em/en dashes anywhere in this file.
 *  - No eslint-disable comments.
 *  - No "photo"/"image" in HTML alt attributes.
 *  - Every clause carries a stable data-testid.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/utils/api';
import { ShieldAlert } from 'lucide-react';

const CLAUSE_ANCHORS = [
  {
    id: 'aup-clause-sexually-explicit',
    label: '1. Sexually explicit profile content',
    heading: '1. Sexually explicit profile content',
  },
  {
    id: 'aup-clause-csam',
    label: '2. Child sexual exploitation or abuse',
    heading: '2. Child sexual exploitation or abuse (CSAM)',
  },
  {
    id: 'aup-clause-sexual-violence',
    label: '3. Sexual violence',
    heading: '3. Sexual violence',
  },
  {
    id: 'aup-clause-age-requirement',
    label: '4. Age requirement',
    heading: '4. Age requirement',
  },
];

function anchorIdForHeading(text) {
  const match = CLAUSE_ANCHORS.find((c) => c.heading === text.trim());
  if (match) return match.id;
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function RenderAUP({ md }) {
  const blocks = useMemo(() => {
    if (!md) return [];
    const lines = md.split('\n');
    const out = [];
    let paragraph = [];
    const flushParagraph = () => {
      if (paragraph.length) {
        out.push({ type: 'p', text: paragraph.join(' ').trim() });
        paragraph = [];
      }
    };
    for (const raw of lines) {
      const line = raw.replace(/\r$/, '');
      if (!line.trim()) { flushParagraph(); continue; }
      if (line.startsWith('# ')) { flushParagraph(); out.push({ type: 'h1', text: line.slice(2) }); continue; }
      if (line.startsWith('## ')) {
        flushParagraph();
        const label = line.slice(3).trim();
        out.push({ type: 'h2', text: label, id: anchorIdForHeading(label) });
        continue;
      }
      paragraph.push(line);
    }
    flushParagraph();
    return out;
  }, [md]);

  const inline = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
      if (/^\*\*[^*]+\*\*$/.test(p)) {
        return <strong key={i} className="text-primary-900">{p.slice(2, -2)}</strong>;
      }
      const linkParts = [];
      let last = 0;
      const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
      let m;
      while ((m = linkRe.exec(p)) !== null) {
        if (m.index > last) linkParts.push(p.slice(last, m.index));
        linkParts.push(<a key={`l${i}-${m.index}`} href={m[2]} className="text-secondary-700 underline hover:text-secondary-800">{m[1]}</a>);
        last = m.index + m[0].length;
      }
      if (last < p.length) linkParts.push(p.slice(last));
      const remainder = linkParts.length ? linkParts : [p];
      return (
        <React.Fragment key={i}>
          {remainder.map((chunk, cIdx) => {
            if (typeof chunk !== 'string') return chunk;
            if (chunk.includes('<a href="mailto:')) {
              return (
                <span
                  key={cIdx}
                  dangerouslySetInnerHTML={{ __html: chunk }}
                />
              );
            }
            return <React.Fragment key={cIdx}>{chunk}</React.Fragment>;
          })}
        </React.Fragment>
      );
    });
  };

  // Bundle 138 patch (post WARN): group each `## clause` heading with
  // the paragraphs that follow it into a single <section> wrapper
  // that carries the clause testid, so DOM assertions can query the
  // full clause block rather than just the heading.
  const grouped = [];
  let currentGroup = null;
  const pushGroup = () => {
    if (currentGroup) {
      grouped.push(currentGroup);
      currentGroup = null;
    }
  };
  for (const b of blocks) {
    if (b.type === 'h1') {
      pushGroup();
      grouped.push({ kind: 'h1', node: b });
      continue;
    }
    if (b.type === 'h2') {
      pushGroup();
      currentGroup = { kind: 'section', heading: b, paras: [] };
      continue;
    }
    // paragraph
    if (currentGroup) {
      currentGroup.paras.push(b);
    } else {
      grouped.push({ kind: 'p', node: b });
    }
  }
  pushGroup();

  return (
    <div className="text-primary-800 leading-relaxed">
      {grouped.map((g, i) => {
        if (g.kind === 'h1') {
          return (
            <h1 key={`h1-${i}`} className="font-display text-3xl sm:text-4xl font-bold text-primary-900 mt-2 mb-4">
              {g.node.text}
            </h1>
          );
        }
        if (g.kind === 'p') {
          return <p key={`p-${i}`} className="my-3 text-primary-700">{inline(g.node.text)}</p>;
        }
        // section wrapper - heading + paragraphs share a data-testid
        const h = g.heading;
        const isClauseAnchor = h.id.startsWith('aup-clause-');
        return (
          <section
            key={`sec-${i}`}
            data-testid={isClauseAnchor ? h.id : `aup-section-${h.id}`}
            className="scroll-mt-24"
          >
            <h2
              id={h.id}
              data-testid={isClauseAnchor ? `${h.id}-heading` : `aup-h-${h.id}`}
              className="font-display text-lg font-semibold text-primary-900 mt-10 mb-3"
            >
              {h.text}
            </h2>
            {g.paras.map((b, j) => (
              <p key={`sp-${i}-${j}`} className="my-3 text-primary-700">
                {inline(b.text)}
              </p>
            ))}
          </section>
        );
      })}
    </div>
  );
}

const AcceptableUsePolicyPage = () => {
  const [md, setMd] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get('/legal/acceptable-use')
      .then((r) => {
        if (!mounted) return;
        setMd(r.data?.markdown || '');
        setUpdatedAt(r.data?.updated_at || null);
      })
      .catch(() => { if (mounted) setMd(''); });
    return () => { mounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="acceptable-use-page">
      {/* Crawler-visible fallback: the four Stripe-compliance clause
          phrases must be present in the initial HTML even when JS is
          disabled. Do NOT remove. */}
      <div className="sr-only" aria-hidden="false" data-testid="aup-compliance-fallback">
        Dostination prohibits sexually explicit profile content, child sexual exploitation or abuse (CSAM),
        sexual violence, and any user under the age of 18. Reports of CSAM are forwarded to the National
        Center for Missing and Exploited Children and to law enforcement.
      </div>

      <div className="border-b border-neutral-100 bg-neutral-50">
        <div className="container mx-auto px-4 md:px-8 py-10">
          <div className="flex items-center gap-3 text-secondary-800 text-xs uppercase tracking-widest font-medium">
            <ShieldAlert className="h-4 w-4" /> Community content policy
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-primary-900 mt-3">Acceptable Use Policy</h1>
          <p className="text-primary-700 mt-3 max-w-3xl">
            Content and conduct rules that apply everywhere on Dostination: profiles, testimonials, event pages, RSVP
            answers, uploads, messages, and every user-facing surface. Violations are removed on sight.
          </p>
          {updatedAt ? (
            <p className="text-xs text-neutral-500 mt-2" data-testid="aup-updated-at">
              Last updated {new Date(updatedAt).toLocaleDateString()}
            </p>
          ) : null}
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-10 grid gap-10 md:grid-cols-[240px_1fr]">
        <aside className="hidden md:block">
          <div className="sticky top-24">
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">On this page</div>
            <ul className="space-y-1.5 text-sm">
              {CLAUSE_ANCHORS.map((c) => (
                <li key={c.id}>
                  <a href={`#${c.id}`} className="text-primary-700 hover:text-primary-900" data-testid={`aup-toc-${c.id}`}>
                    {c.label}
                  </a>
                </li>
              ))}
              <li className="pt-3 mt-3 border-t border-neutral-100">
                <a href="/terms" className="text-secondary-800 hover:text-secondary-900 font-medium" data-testid="aup-toc-terms">
                  Terms and Conditions
                </a>
              </li>
            </ul>
          </div>
        </aside>

        <article className="max-w-3xl">
          {md ? <RenderAUP md={md} /> : (
            <div className="text-neutral-500 text-sm">Loading policy...</div>
          )}
          <p className="text-[11px] text-neutral-500 mt-10 border-t border-neutral-100 pt-4">
            This document is a general template and not legal advice. Consult with your legal counsel
            before relying on it for enforcement in your jurisdiction.
          </p>
        </article>
      </div>
    </div>
  );
};

export default AcceptableUsePolicyPage;
